import { useUser } from "src/contexts/user-context";
import { useDevSettings } from "src/pages/agents/hooks/use-dev-settings";

import type { ContentEmailsBoolExp, SavedMessagesBoolExp } from "src/graphql";

/**
 * Filter clause that excludes internal-only (private) messages. Apply it to
 * every query that lists customer-facing messages — both `content_emails`
 * and `saved_messages` expose the same `is_private` column.
 */
export const NOT_PRIVATE_FILTER: ContentEmailsBoolExp & SavedMessagesBoolExp = {
  is_private: { _neq: true },
};

/**
 * Private messages are a dev-only concept: only HT admins in dev mode see
 * them (mirrors the campaigns `is_private` gating). Everyone else must have
 * them filtered from every message listing via NOT_PRIVATE_FILTER.
 */
export function useCanSeePrivateMessages(): boolean {
  const { user } = useUser();
  const {
    settings: { isDevMode },
  } = useDevSettings();
  return user?.can_impersonate === true && isDevMode;
}

/**
 * Scopes a saved-messages filter to visible (non-private) rows unless the
 * viewer can see private messages. Callers that share a react-query cache
 * entry (the Messages tab index and the folder counts) must both build their
 * filter through this so the variable shapes — and thus the query keys —
 * stay identical.
 */
export function buildVisibilityScopedFilter(
  filter: SavedMessagesBoolExp,
  canSeePrivateMessages: boolean,
): SavedMessagesBoolExp {
  if (canSeePrivateMessages) return filter;
  if (Object.keys(filter).length === 0) return NOT_PRIVATE_FILTER;
  return { _and: [filter, NOT_PRIVATE_FILTER] };
}
